/**
 * Der Teil „Gemini-Schlüssel“ in der Karte „Modell-Zugang“ (Einstellungen → Modelle und Konto). (25.10.2026)
 *
 * Bilder, Musik, Video-Analyse und Übersetzen laufen über Gemini. Jedes Konto hinterlegt hier seinen EIGENEN Schlüssel; der Server
 * nimmt ihn (server/gemini-zugang.ts, geminiSchluesselFuer). Ohne eigenen arbeitet nur das Hauptkonto mit dem Schlüssel des Servers.
 *
 * ⚠️ Gleiche Regeln wie beim Claude-Schlüssel (modell-zugang-karte.ts): Passwortfeld ohne Autovervollständigung, nach dem Senden sofort
 *    leer, die Seite legt ihn nirgends ab, und zurück kommen nur die letzten vier Zeichen.
 */
import { endetAufAnzeige } from "../../shared/api-zugang.js";

export interface GeminiStand {
  gespeichert: boolean;
  endetAuf: string | null;
  geprueftAmMs: number | null;
  geprueftWort: string | null;
  geprueftSatz: string | null;
  /** Womit Gemini-Aufträge dieses Kontos JETZT laufen. */
  quelle: "eigen" | "server" | "fehlt";
}

type Ton = "is-ok" | "is-warn" | "is-off" | "is-fehler";

/** Die Statuszeile des Gemini-Teils — ein Satz, keine Technik. */
export function geminiStatusText(g: GeminiStand | null | undefined, vorZeit: (ms: number) => string): { text: string; ton: Ton } {
  if (!g) return { text: "Der Stand ist gerade nicht abrufbar.", ton: "is-warn" };
  if (!g.gespeichert) {
    return g.quelle === "server"
      ? { text: "Kein eigener Schlüssel — Vespera nutzt den Schlüssel des Servers", ton: "is-off" }
      : { text: "Kein Schlüssel gespeichert — Bilder, Musik und Videos gehen erst mit deinem Schlüssel", ton: "is-off" };
  }
  const ende = endetAufAnzeige(g.endetAuf);
  const geprueft = g.geprueftAmMs ? `, zuletzt geprüft ${vorZeit(g.geprueftAmMs)}` : "";
  const gut = g.geprueftWort === "ok";
  return { text: `Schlüssel gespeichert, endet auf ${ende}${geprueft}${gut ? "" : " — bitte erneut prüfen"}`, ton: gut ? "is-ok" : "is-warn" };
}

/** Die Zeile unter dem Feld: nur die LÄNGE des Eingefügten, nie der Schlüssel selbst. */
export function geminiEingabeHinweis(laenge: number, anfangOk: boolean): { text: string; warnt: boolean } {
  if (laenge === 0) return { text: "", warnt: false };
  const eingefuegt = `Eingefügt — ${laenge} Zeichen`;
  return anfangOk ? { text: eingefuegt, warnt: false } : { text: `${eingefuegt}. Das sieht nicht wie ein Gemini-Schlüssel aus (er beginnt mit AIza).`, warnt: true };
}

const LINK_ERSTELLEN = "https://aistudio.google.com/apikey";
const SATZ_NETZ = "Der Server ist gerade nicht erreichbar. Bitte versuch es gleich noch einmal.";

interface Antwort {
  ok: boolean;
  status: number;
  daten: { gemini?: GeminiStand; satz?: string; error?: string } | null;
}

interface GeminiBlockDeps {
  /** POST /api/api-zugang/<pfad> mit dem Herkunftskopf der Karte. */
  sende: (pfad: string, koerper: unknown) => Promise<Antwort>;
  el: <K extends keyof HTMLElementTagNameMap>(tag: K, klasse: string, text?: string) => HTMLElementTagNameMap[K];
  vorZeit: (ms: number) => string;
  /** Die Statuszeile setzen (Marke + Satz, „····“ mit Luft) — dieselbe Zeichnung wie oben in der Karte. */
  setzeStatus: (ziel: HTMLElement, ton: Ton, text: string, ende?: string) => void;
}

export function baueGeminiSchluessel(d: GeminiBlockDeps): { el: HTMLElement; zeige: (g: GeminiStand | null | undefined) => void } {
  const { el } = d;
  const wrap = el("section", "modell-zugang-gemini");
  wrap.setAttribute("aria-labelledby", "modell-zugang-gemini-titel");
  const titel = el("h4", "modell-zugang-gemini-titel", "Gemini-Schlüssel");
  titel.id = "modell-zugang-gemini-titel";
  wrap.append(titel, el("p", "modell-zugang-klein", "Für Bilder, Musik, Videos und Übersetzungen."));

  const status = el("div", "connector-status modell-zugang-status");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.textContent = "Status wird geladen …";
  wrap.appendChild(status);

  const form = el("form", "panel-karte-form modell-zugang-form");
  form.setAttribute("autocomplete", "off");
  const label = el("label", "panel-karte-label", "Gemini-Schlüssel einfügen");
  label.htmlFor = "modell-zugang-gemini-feld";
  const feld = el("input", "panel-karte-feld modell-zugang-feld");
  feld.id = "modell-zugang-gemini-feld";
  feld.type = "password";
  feld.autocomplete = "off";
  feld.spellcheck = false;
  feld.setAttribute("autocapitalize", "off");
  feld.setAttribute("autocorrect", "off");
  feld.setAttribute("data-1p-ignore", "");
  feld.setAttribute("data-lpignore", "true");
  feld.name = "gemini-zugang-" + Math.random().toString(36).slice(2, 8);
  feld.placeholder = "AIza…";
  const eingabeZeile = el("p", "modell-zugang-eingabe");
  eingabeZeile.id = "modell-zugang-gemini-eingabe";
  eingabeZeile.setAttribute("role", "status");
  eingabeZeile.setAttribute("aria-live", "polite");
  feld.setAttribute("aria-describedby", eingabeZeile.id);
  const speichern = el("button", "connector-connect modell-zugang-speichern", "Speichern und prüfen");
  speichern.type = "submit";
  speichern.setAttribute("aria-label", "Gemini-Schlüssel speichern und prüfen");
  const erstellen = el("a", "modell-zugang-link", "Schlüssel erstellen");
  erstellen.href = LINK_ERSTELLEN;
  erstellen.target = "_blank";
  erstellen.rel = "noopener noreferrer";
  erstellen.setAttribute("aria-label", "Gemini-Schlüssel erstellen (öffnet Google AI Studio)");
  const reihe = el("div", "modell-zugang-reihe");
  reihe.append(speichern, erstellen);
  form.append(label, feld, eingabeZeile, reihe);
  wrap.appendChild(form);
  wrap.appendChild(el("p", "panel-karte-hinweis", "Der Schlüssel liegt verschlüsselt auf dem Server; Google berechnet die Kosten direkt dir."));

  const aktionen = el("div", "connector-actions modell-zugang-aktionen");
  const pruefen = el("button", "connector-disconnect modell-zugang-knopf", "Erneut prüfen");
  pruefen.type = "button";
  const entfernen = el("button", "connector-disconnect modell-zugang-knopf", "Schlüssel löschen");
  entfernen.type = "button";
  entfernen.setAttribute("aria-label", "Gemini-Schlüssel löschen");
  pruefen.setAttribute("aria-label", "Gemini-Schlüssel erneut prüfen");
  aktionen.append(pruefen, entfernen);
  wrap.appendChild(aktionen);

  const zeige = (g: GeminiStand | null | undefined): void => {
    const { text, ton } = geminiStatusText(g, d.vorZeit);
    d.setzeStatus(status, ton, text, g ? endetAufAnzeige(g.endetAuf) : "");
    const da = g?.gespeichert === true;
    aktionen.hidden = !da;
  };
  const gut = (text: string): void => d.setzeStatus(status, "is-ok", text);
  const fehler = (text: string): void => d.setzeStatus(status, "is-fehler", text);

  const zeigeEingabe = (): void => {
    const wert = feld.value.trim();
    const h = geminiEingabeHinweis(wert.length, wert.startsWith("AIza"));
    eingabeZeile.textContent = h.text;
    eingabeZeile.classList.toggle("is-warnt", h.warnt);
  };
  feld.addEventListener("input", zeigeEingabe);
  feld.addEventListener("change", zeigeEingabe);
  feld.addEventListener("paste", () => window.setTimeout(zeigeEingabe, 0));

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const wert = feld.value.trim();
    if (!wert) {
      fehler("Bitte füge zuerst deinen Gemini-Schlüssel ein.");
      return;
    }
    // Sofort leeren: der Schlüssel bleibt nicht länger im Feld stehen als nötig.
    feld.value = "";
    zeigeEingabe();
    speichern.disabled = true;
    d.setzeStatus(status, "is-warn", "Prüfe …");
    void d.sende("gemini-schluessel", { schluessel: wert }).then((r) => {
      speichern.disabled = false;
      if (r.ok && r.daten?.gemini) {
        zeige(r.daten.gemini);
        const ende = endetAufAnzeige(r.daten.gemini.endetAuf);
        d.setzeStatus(status, "is-ok", `Gespeichert und geprüft — endet auf ${ende}`, ende);
      } else {
        fehler(r.daten?.satz ?? (r.status === 429 ? "Bitte warte eine Minute, dann geht es weiter." : r.status === 0 ? SATZ_NETZ : "Das hat nicht geklappt. Der Schlüssel wurde nicht gespeichert."));
      }
    });
  });

  pruefen.addEventListener("click", () => {
    pruefen.disabled = true;
    d.setzeStatus(status, "is-warn", "Prüfe …");
    void d.sende("gemini-pruefen", {}).then((r) => {
      pruefen.disabled = false;
      if (r.daten?.gemini) zeige(r.daten.gemini);
      const satz = r.daten?.satz ?? (r.status === 429 ? "Bitte warte eine Minute, dann geht es weiter." : SATZ_NETZ);
      if (r.ok && r.daten?.gemini?.geprueftWort === "ok") gut(satz);
      else fehler(satz);
    });
  });

  entfernen.addEventListener("click", () => {
    void (async () => {
      const { confirmSheet } = await import("./sheet-ui.js");
      const ja = await confirmSheet({ title: "Gemini-Schlüssel löschen?", confirmLabel: "Löschen", danger: true });
      if (!ja) return;
      const r = await d.sende("gemini-entfernen", {});
      if (r.ok && r.daten?.gemini) {
        zeige(r.daten.gemini);
        gut("Der Gemini-Schlüssel ist gelöscht.");
      } else fehler(SATZ_NETZ);
    })();
  });

  return { el: wrap, zeige };
}
